import React from "react";
import { Link } from "react-router-dom";
import {
  Shield, Activity, Users, GraduationCap, LifeBuoy, FlaskConical, ArrowUpRight, ArrowRight,
  CheckCircle2, AlertTriangle, Phone, Brain, Cpu, Database, Zap, Eye, Network, FileLock2,
  BadgeCheck, ShieldCheck, Sparkles, BellRing, Lock, Radar, BookOpen, IndianRupee
} from "lucide-react";
import PartnerBar from "@/components/PartnerBar";
import SectionHeading from "@/components/SectionHeading";
import FinalCTA from "@/components/FinalCTA";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/41c29f5c-93a5-481f-b483-b658b85e9b47/images/ff64dcae157dffad809b8d3f905c61f6646d68c76d698abc548727dee6936d77.png";
const AWARE_IMG = "https://images.unsplash.com/photo-1640622302099-f10b18de7b16?auto=format&fit=crop&w=1400&q=80";
const WORKFORCE_IMG = "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=1400&q=80";

const ECOSYSTEM = [
  { to: "/enterprise-security", icon: Shield, title: "Enterprise Security", tag: "Division 01",
    desc: "VAPT, endpoint, SOC, DLP, ZTNA and managed security for regulated enterprises.",
    accent: "from-[#3B82F6]/15 to-[#6D28D9]/15", iconColor: "text-[#3B82F6]" },
  { to: "/awareness", icon: Users, title: "Cyber Awareness", tag: "Division 02",
    desc: "A digital safety movement — for citizens, seniors, schools and merchants.",
    accent: "from-cyan-400/15 to-blue-400/15", iconColor: "text-cyan-600" },
  { to: "/workforce", icon: GraduationCap, title: "Workforce Development", tag: "Division 03",
    desc: "Cyber talent infrastructure — SOC analysts, deployment engineers, interns and program coordinators.",
    accent: "from-amber-300/20 to-orange-400/10", iconColor: "text-amber-600" },
  { to: "/training", icon: BookOpen, title: "Corporate Training", tag: "Division 04",
    desc: "Enterprise security training, phishing simulation, leadership awareness and compliance learning.",
    accent: "from-emerald-300/20 to-teal-400/15", iconColor: "text-emerald-600" },
  { to: "/assistance", icon: LifeBuoy, title: "Cyber Assistance", tag: "Division 05",
    desc: "Guidance, reporting help and digital safety response for individuals and small teams.",
    accent: "from-rose-300/20 to-pink-400/15", iconColor: "text-rose-600" },
  { to: "/labs", icon: FlaskConical, title: "Future SOC & Labs", tag: "Division 06",
    desc: "AI-assisted detection, threat intelligence and the long-horizon security research roadmap.",
    accent: "from-violet-400/20 to-fuchsia-400/15", iconColor: "text-violet-600" },
];

const SECURITY_STACK = [
  { icon: Radar, title: "VAPT", desc: "Web, network, API and mobile penetration testing with prioritised remediation." },
  { icon: Shield, title: "Endpoint Security", desc: "EDR/XDR with auto-isolation, ransomware rollback and managed tuning." },
  { icon: FileLock2, title: "Data Protection (DLP)", desc: "Stop data exfiltration across endpoint, email and cloud channels." },
  { icon: Activity, title: "SOC Services", desc: "24/7 monitoring, threat hunting, triage and incident response." },
  { icon: BadgeCheck, title: "Security Audits", desc: "Configuration, posture and process audits aligned to ISO 27001 and DPDP." },
  { icon: Lock, title: "Managed Security", desc: "Cosmos Defence runs your security stack end-to-end as an extension of your team." },
  { icon: ShieldCheck, title: "Compliance & GRC", desc: "DPDP, SOC 2 and policy automation with audit-ready evidence." },
  { icon: Network, title: "Zero Trust", desc: "ZTNA replacing legacy VPN — least-privilege access for hybrid workforces." },
];

const AWARENESS_POINTS = [
  { icon: AlertTriangle, label: "UPI & QR Code Fraud" },
  { icon: Eye, label: "Phishing & Social Engineering" },
  { icon: Phone, label: "1930 Helpline Awareness" },
  { icon: Users, label: "Senior Citizen Safety" },
  { icon: GraduationCap, label: "School & College Programs" },
  { icon: IndianRupee, label: "Digital Fraud Prevention" },
];

const WORKFORCE_PILLARS = [
  { title: "SOC Analyst Training", desc: "Hands-on tier-1 and tier-2 SOC labs with real telemetry." },
  { title: "Skill India Aligned", desc: "Curriculum mapped to national skilling frameworks." },
  { title: "Internship Pipeline", desc: "Structured 3–6 month internships across SOC, awareness and engineering." },
  { title: "Industry Readiness", desc: "Soft-skills, reporting, customer interaction and on-the-job rotations." },
];

const LAB_FEEDS = [
  { tag: "Threat Alert", title: "UPI 'fake payment screen' wave hits Tier-2 retail", time: "2h ago" },
  { tag: "AI Security", title: "Deepfake voice clones now targeting Indian CFOs", time: "1d ago" },
  { tag: "Awareness", title: "New senior-citizen scam: fake electricity disconnection calls", time: "2d ago" },
  { tag: "Compliance", title: "DPDP rules — Data Fiduciary classification draft analysis", time: "4d ago" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden pt-28 pb-12 lg:pt-32 lg:pb-20">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="absolute inset-0 radial-glow" />
        <div className="relative container-cd">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="badge-cd" data-testid="hero-eyebrow">
                <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" /> Cybersecurity · Awareness · Workforce Ecosystem
              </span>
              <h1 data-testid="hero-headline" className="mt-6 font-display text-4xl md:text-5xl lg:text-[60px] font-extrabold tracking-tight text-[#0A1B5E] leading-[1.04]">
                Building India's<br />
                <span className="gradient-text">Next-Generation</span><br />
                Cybersecurity Ecosystem
              </h1>
              <p data-testid="hero-subtext" className="mt-7 text-lg text-slate-600 leading-relaxed max-w-2xl">
                Enterprise cybersecurity, awareness initiatives, workforce development, and future-ready AI security infrastructure — operating as a single integrated ecosystem.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link to="/contact?type=consultation" className="btn-accent" data-testid="hero-cta-consultation">
                  Request Consultation <ArrowUpRight size={18} />
                </Link>
                <Link to="/ecosystem" className="btn-primary" data-testid="hero-cta-ecosystem">
                  Explore the Ecosystem
                </Link>
                <Link to="/enterprise-security" className="underline-link inline-flex items-center gap-1" data-testid="hero-explore-link">
                  Enterprise Security Stack <ArrowRight size={16} />
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-500 font-mono-tech tracking-wider">
                <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#3B82F6]" /> ISO 27001 Aligned</span>
                <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#3B82F6]" /> DPDP Ready</span>
                <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#3B82F6]" /> 24/7 SOC Operations</span>
                <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#3B82F6]" /> Skill India Aligned</span>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="absolute -inset-6 bg-gradient-to-tr from-cyan-200/40 via-blue-200/20 to-purple-200/40 rounded-[2rem] blur-2xl" />
              <div className="relative rounded-[2rem] overflow-hidden border border-slate-200/60 bg-white shadow-[0_30px_80px_-30px_rgba(10,27,94,0.25)]">
                <img src={HERO_IMG} alt="Cosmos Defence AI cybersecurity shield" className="w-full h-auto animate-floaty" data-testid="hero-image" />
                <div className="absolute top-5 left-5 glass-card rounded-2xl px-4 py-3 flex items-center gap-3">
                  <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse-ring" />
                  <div className="leading-tight">
                    <div className="font-mono-tech text-[10px] tracking-widest text-slate-500 uppercase">SOC Status</div>
                    <div className="font-display font-bold text-sm text-[#0A1B5E]">All Systems Secure</div>
                  </div>
                </div>
                <div className="absolute bottom-5 right-5 glass-card rounded-2xl px-4 py-3">
                  <div className="font-mono-tech text-[10px] tracking-widest text-slate-500 uppercase">Active Divisions</div>
                  <div className="font-display font-bold text-xl text-[#0A1B5E]">6 / 6 Online</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PartnerBar />

      {/* ECOSYSTEM ARCHITECTURE — the brand identity */}
      <section data-testid="ecosystem-section" className="py-24 bg-[#F8FAFC] relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative container-cd">
          <SectionHeading
            eyebrow="The Ecosystem"
            title="Six divisions. One mission. A complete cybersecurity ecosystem."
            description="Cosmos Defence is not a single-product company. It's an integrated ecosystem protecting enterprises, building cyber talent, and driving digital safety across India."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ECOSYSTEM.map((d, i) => (
              <Link to={d.to} key={d.title} data-testid={`ecosystem-card-${i}`} className="group relative card-cd overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${d.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <span className={`h-12 w-12 grid place-items-center rounded-xl bg-white border border-slate-200 ${d.iconColor}`}>
                      <d.icon size={22} strokeWidth={1.6} />
                    </span>
                    <span className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-slate-400">{d.tag}</span>
                  </div>
                  <h3 className="mt-6 font-display text-xl font-bold text-[#0A1B5E]">{d.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed text-[15px]">{d.desc}</p>
                  <span className="mt-6 inline-flex items-center gap-1 text-[#3B82F6] font-semibold text-sm group-hover:gap-2 transition-all">
                    Enter division <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ENTERPRISE SECURITY STACK */}
      <section data-testid="security-stack-section" className="py-24 bg-white">
        <div className="container-cd">
          <SectionHeading
            eyebrow="Enterprise Security Stack"
            title="Operational security, end-to-end"
            description="A composable stack of capabilities — deploy what you need today, expand as you scale."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {SECURITY_STACK.map((s, i) => (
              <Link to="/enterprise-security" key={s.title} data-testid={`stack-card-${i}`} className="card-cd group !p-6">
                <span className="h-11 w-11 grid place-items-center rounded-xl bg-blue-50 border border-blue-100 text-[#3B82F6]">
                  <s.icon size={20} strokeWidth={1.6} />
                </span>
                <h3 className="mt-5 font-display text-base font-bold text-[#0A1B5E]">{s.title}</h3>
                <p className="mt-2 text-slate-600 text-[13.5px] leading-relaxed">{s.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-[#3B82F6] font-semibold text-xs group-hover:gap-2 transition-all">
                  Learn more <ArrowRight size={12} />
                </span>
              </Link>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/enterprise-security" className="btn-primary" data-testid="stack-view-all">
              View Full Enterprise Stack <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CYBER AWARENESS INITIATIVE — emotional + human */}
      <section data-testid="awareness-section" className="py-24 bg-[#F8FAFC] relative overflow-hidden">
        <div className="container-cd">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-cyan-200/40 to-blue-200/40 rounded-[2rem] blur-2xl" />
              <div className="relative rounded-[1.75rem] overflow-hidden border border-slate-200/60 shadow-[0_30px_80px_-30px_rgba(10,27,94,0.25)]">
                <img src={AWARE_IMG} alt="Diverse family digital safety" className="w-full h-[460px] object-cover" data-testid="awareness-image" />
              </div>
              <div className="absolute -bottom-6 -right-4 glass-card rounded-2xl p-5 max-w-[260px] hidden md:block">
                <BellRing size={20} className="text-[#6D28D9]" />
                <div className="mt-2 font-display font-bold text-[#0A1B5E] text-lg">Victim of Cyber Fraud?</div>
                <div className="text-xs text-slate-500 mt-1">Report immediately on</div>
                <div className="font-mono-tech text-2xl font-extrabold text-[#0A1B5E] mt-1">1930</div>
              </div>
            </div>

            <div>
              <span className="badge-cd">A Digital Safety Movement</span>
              <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0A1B5E] leading-tight">
                Cyber safety isn't optional.<br />It belongs to everyone.
              </h2>
              <p className="mt-5 text-slate-600 text-lg leading-relaxed">
                We run workshops, campaigns and 1930-reporting drives — across schools, RWAs, senior-citizen groups, merchants and corporates — to make digital safety a household conversation.
              </p>
              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {AWARENESS_POINTS.map((a, i) => (
                  <li key={a.label} data-testid={`awareness-point-${i}`} className="flex items-center gap-3">
                    <span className="h-10 w-10 grid place-items-center rounded-xl bg-white border border-slate-200 shrink-0">
                      <a.icon size={16} className="text-[#3B82F6]" strokeWidth={1.6} />
                    </span>
                    <span className="text-[#0A1B5E] font-display font-semibold text-sm">{a.label}</span>
                  </li>
                ))}
              </ul>
              <Link to="/awareness" className="btn-primary mt-10" data-testid="awareness-cta">
                Explore Awareness Programs <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CYBER WORKFORCE & SKILL DEVELOPMENT */}
      <section data-testid="workforce-section" className="py-24 bg-white">
        <div className="container-cd">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="badge-cd">Cyber Talent Infrastructure</span>
              <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0A1B5E] leading-tight">
                Building the next generation of cyber defenders
              </h2>
              <p className="mt-5 text-slate-600 text-lg leading-relaxed max-w-xl">
                A workforce ecosystem — not a job board. Cosmos Defence trains, deploys and sustains the people who run modern cybersecurity operations.
              </p>
              <ul className="mt-8 space-y-4">
                {WORKFORCE_PILLARS.map((p, i) => (
                  <li key={p.title} data-testid={`workforce-pillar-${i}`} className="flex items-start gap-4">
                    <span className="h-10 w-10 grid place-items-center rounded-xl gradient-cta text-white shrink-0">
                      <GraduationCap size={18} />
                    </span>
                    <div>
                      <div className="font-display font-bold text-[#0A1B5E]">{p.title}</div>
                      <div className="text-sm text-slate-600 mt-0.5">{p.desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/workforce" className="btn-accent" data-testid="workforce-cta">
                  Cyber Workforce Ecosystem <ArrowUpRight size={16} />
                </Link>
                <Link to="/careers" className="btn-ghost" data-testid="workforce-careers-cta">Join the Pipeline</Link>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-amber-200/40 to-rose-200/30 rounded-[2rem] blur-2xl" />
              <div className="relative rounded-[1.75rem] overflow-hidden border border-slate-200/60">
                <img src={WORKFORCE_IMG} alt="Cybersecurity workforce training" className="w-full h-[460px] object-cover" data-testid="workforce-image" />
                <div className="absolute top-5 left-5 glass-card rounded-2xl px-4 py-3">
                  <div className="font-mono-tech text-[10px] tracking-widest text-slate-500 uppercase">Active Programs</div>
                  <div className="font-display font-bold text-sm text-[#0A1B5E]">SOC · Awareness · Deployment</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CORPORATE TRAINING + ASSISTANCE — two-up summary */}
      <section data-testid="training-assist-section" className="py-24 bg-[#F8FAFC]">
        <div className="container-cd grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Link to="/training" data-testid="training-summary-card" className="card-cd !p-10 group">
            <span className="h-12 w-12 grid place-items-center rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-600">
              <BookOpen size={22} strokeWidth={1.6} />
            </span>
            <h3 className="mt-6 font-display text-2xl font-bold text-[#0A1B5E]">Corporate Security Training</h3>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Continuous awareness, phishing simulation, leadership cyber programs and compliance learning — designed to make security culture measurable.
            </p>
            <span className="mt-6 inline-flex items-center gap-1 text-[#3B82F6] font-semibold text-sm group-hover:gap-2 transition-all">
              See training programs <ArrowRight size={14} />
            </span>
          </Link>
          <Link to="/assistance" data-testid="assistance-summary-card" className="card-cd !p-10 group">
            <span className="h-12 w-12 grid place-items-center rounded-xl bg-rose-50 border border-rose-100 text-rose-600">
              <LifeBuoy size={22} strokeWidth={1.6} />
            </span>
            <h3 className="mt-6 font-display text-2xl font-bold text-[#0A1B5E]">Cyber Assistance & Support</h3>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Guidance for individuals, reporting help for fraud victims, and digital safety response for small teams — bridging citizens and the 1930 ecosystem.
            </p>
            <span className="mt-6 inline-flex items-center gap-1 text-[#3B82F6] font-semibold text-sm group-hover:gap-2 transition-all">
              Get assistance <ArrowRight size={14} />
            </span>
          </Link>
        </div>
      </section>

      {/* FUTURE SOC & LABS — dark futuristic */}
      <section data-testid="labs-section" className="relative overflow-hidden">
        <div className="dark-strip-bg text-white py-24 lg:py-28">
          <div className="container-cd">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
              <div>
                <span className="badge-cd !bg-white/10 !text-cyan-300 !border-white/15">Future SOC · Security Labs</span>
                <h2 className="mt-5 font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
                  The future of defence will be <span className="bg-gradient-to-r from-cyan-300 via-blue-300 to-purple-300 bg-clip-text text-transparent">AI-assisted, autonomous and observable.</span>
                </h2>
                <p className="mt-5 text-slate-300 text-lg leading-relaxed max-w-xl">
                  Cosmos Defence Labs is our long-horizon research arm — building the AI detection, automation and threat-intelligence playbooks that tomorrow's SOC will run on.
                </p>
                <ul className="mt-8 space-y-3">
                  {[
                    { icon: Brain, t: "AI-Assisted Detection & Triage" },
                    { icon: Cpu, t: "Security Automation & Response" },
                    { icon: Database, t: "Threat Intelligence Pipelines" },
                    { icon: Zap, t: "Deepfake & Synthetic Fraud Models" },
                  ].map((r) => (
                    <li key={r.t} className="flex items-center gap-3 text-slate-200">
                      <span className="h-9 w-9 grid place-items-center rounded-lg bg-white/5 border border-white/10 text-cyan-300">
                        <r.icon size={16} />
                      </span>
                      <span className="font-display font-medium">{r.t}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/labs" className="btn-accent mt-10" data-testid="labs-cta">
                  Enter the Labs <ArrowUpRight size={16} />
                </Link>
              </div>

              <div className="relative" data-testid="threat-feed">
                <div className="absolute -inset-6 bg-cyan-400/10 blur-3xl rounded-[2rem]" />
                <div className="relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-cyan-200">Threat Intelligence Feed</span>
                    </div>
                    <Sparkles size={14} className="text-cyan-300" />
                  </div>
                  <ul className="divide-y divide-white/5">
                    {LAB_FEEDS.map((f, i) => (
                      <li key={f.title} data-testid={`threat-feed-item-${i}`} className="px-6 py-4 flex items-start gap-4 hover:bg-white/5 transition-colors">
                        <span className="px-2 py-1 rounded-full bg-white/10 text-cyan-200 text-[10px] font-mono-tech tracking-widest uppercase shrink-0">{f.tag}</span>
                        <div className="flex-1">
                          <div className="font-display font-semibold text-white text-[15px] leading-snug">{f.title}</div>
                          <div className="text-slate-400 text-xs mt-1 font-mono-tech tracking-wider">{f.time}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <Link to="/blog" data-testid="threat-feed-cta" className="block px-6 py-4 border-t border-white/10 font-display font-semibold text-cyan-300 text-sm hover:text-white transition-colors">
                    Read full intelligence feed →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
